import { apiErrorCode, baseApi, type MoneyDto } from '@shared/api/baseApi';

export type OrderStatus =
  | 'PENDING'
  | 'PAID'
  | 'COMPLETED'
  | 'FAILED'
  | 'REFUNDED'
  /** The number is live and the buyer is waiting for the code. */
  | 'AWAITING_SMS';

/**
 * A rented number, while it is live and afterwards as a record of it.
 *
 * `expiresAt` is absolute rather than a remaining duration: the countdown is
 * rendered on the device, and a duration computed server-side would already be
 * stale by the time the response arrived.
 */
export interface ActivationDto {
  phoneNumber: string;
  expiresAt: string;
}

export interface OrderDto {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  kind: 'PURCHASE' | 'GIVEAWAY';
  pricePaid: MoneyDto;
  status: OrderStatus;
  deliveredItems: unknown[] | null;
  /** The product's redemption steps; shown beside the delivered item. */
  instructions: string | null;
  /** Present on number orders only; drives the live activation screen. */
  activation: ActivationDto | null;
  createdAt: string;
}

export interface PlaceOrderResponse {
  order: {
    id: string;
    productName: string;
    pricePaid: MoneyDto;
    deliveredItems: unknown[];
    /** Paid for, but the shop hands this product over by hand. */
    awaitingDelivery: boolean;
    /** True while an upstream provider is preparing the delivery. */
    providerProcessing: boolean;
    /** Set when the purchase rented a number; the app then shows the session. */
    activation: ActivationDto | null;
    instructions: string | null;
  };
  balance: MoneyDto;
}

export interface CancelActivationResponse {
  order: OrderDto;
  /** False when the code landed first, so the order was delivered instead. */
  refunded: boolean;
  message: string;
}

export const orderApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query<OrderDto[], void>({
      query: () => '/orders',
      transformResponse: (response: { orders: OrderDto[] }) => response.orders,
      providesTags: ['Order'],
    }),
    placeOrder: builder.mutation<
      PlaceOrderResponse,
      {
        productId: string;
        customerInput?: string;
        expectedPriceETB: string;
        idempotencyKey: string;
      }
    >({
      query: ({ idempotencyKey, ...body }) => ({
        url: '/orders',
        method: 'POST',
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      // A confirmed low balance changes nothing. Keep invalidation for other
      // errors because a lost response can leave the order outcome unknown.
      invalidatesTags: (_result, error) =>
        error && apiErrorCode(error) === 'INSUFFICIENT_BALANCE'
          ? []
          : ['Order', 'User', 'Product'],
    }),
    /**
     * One order, polled by the activation screen while a code is in flight.
     *
     * Its own endpoint rather than re-reading the list: this runs every few
     * seconds for everyone mid-verification, and the list carries fifty orders
     * and their products' instructions.
     */
    getOrder: builder.query<OrderDto, string>({
      query: (id) => `/orders/${id}`,
      transformResponse: (response: { order: OrderDto }) => response.order,
      providesTags: (_result, _error, id) => [{ type: 'Order' as const, id }],
    }),
    /**
     * Asks the server to check this order against SMSPool right now.
     *
     * Not a refetch: `getOrder` only reports what the last sweep found, so a
     * button wired to that would spin and change nothing. This one actually
     * reaches upstream and settles the order if the code has landed.
     */
    checkActivation: builder.mutation<OrderDto, string>({
      query: (id) => ({ url: `/orders/${id}/check`, method: 'POST' }),
      transformResponse: (response: { order: OrderDto }) => response.order,
      // A check can deliver the code or refund the money, so both the order
      // list and the wallet may have moved.
      invalidatesTags: (_r, _e, id) => [{ type: 'Order' as const, id }, 'Order', 'User'],
    }),
    cancelActivation: builder.mutation<CancelActivationResponse, string>({
      query: (id) => ({ url: `/orders/${id}/cancel-activation`, method: 'POST' }),
      // A refund puts the money back, so the wallet is stale too.
      invalidatesTags: ['Order', 'User'],
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderQuery,
  usePlaceOrderMutation,
  useCancelActivationMutation,
  useCheckActivationMutation,
} = orderApi;
